import React from 'react';
import PropTypes from 'prop-types';
import FormControl from '../FormControl/FormControl.context';
import FormLabel from '../FormLabel/FormLabel';
import FormInput from '../FormInput/FormInput';
import FormInputLimit from '../FormInputLimit/FormInputLimit';
import { HtmlElement } from '../../prop-types/Element';

/**
 * `TextArea` allows a user to insert multiple lines of text into a UI.
 * It comes with a label, input, and error message support.
 * This component is meant for implementation in a Formik form.
 */
const TextArea = ({
  id,
  name,
  validate,
  innerRef,
  label,
  className,
  limit,
  ...inputProps
}) => (
  <FormControl name={name} validate={validate} containerClass={className}>
    <FormLabel htmlFor={id} disabled={inputProps.disabled}>
      {label}
    </FormLabel>
    {!!limit && <FormInputLimit data-testid="FormInputLimit" limit={limit} />}
    <FormInput
      id={id}
      ref={innerRef}
      as="textarea"
      maxLength={limit}
      rows="3"
      {...inputProps}
    />
  </FormControl>
);

TextArea.propTypes = {
  /**
   * CSS class name
   */
  className: PropTypes.string,
  /**
   * Form ID use to associate the label and input
   * Use specific naming, as a collision can cause undesirable effects.
   */
  id: PropTypes.string.isRequired,
  /**
   * Inner ref; allowing you to interact directly with the Input element.
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(HtmlElement) })
  ]),
  /**
   * Formik field name
   */
  name: PropTypes.string.isRequired,
  /**
   * Formik validation function
   */
  validate: PropTypes.func,
  /**
   * Form label
   */
  label: PropTypes.string.isRequired,
  /**
   * Optional character limit
   */
  limit: PropTypes.number
};

TextArea.defaultProps = {
  className: null,
  innerRef: undefined,
  validate: undefined,
  limit: undefined
};

export default TextArea;
